import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const CONSENT_KEY = "cookie_consent";

type ConsentStatus = "accepted" | "rejected" | null;

export const getConsentStatus = (): ConsentStatus => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(CONSENT_KEY) as ConsentStatus;
};

export const hasAnalyticsConsent = (): boolean => {
  return getConsentStatus() === "accepted";
};

// Disable GA tracking
const disableTracking = () => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
    });
  }
};

// Enable GA tracking
const enableTracking = () => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
    });
  }
};

export const CookieConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const consent = getConsentStatus();
    if (consent === null) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    } else if (consent === "rejected") {
      disableTracking();
    } else if (consent === "accepted") {
      enableTracking();
    }
  }, []);

  const handleClose = (status: ConsentStatus) => {
    setIsClosing(true);
    localStorage.setItem(CONSENT_KEY, status || "");
    
    if (status === "accepted") {
      enableTracking();
    } else {
      disableTracking();
    }

    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 transition-all duration-300 ${
        isClosing ? "translate-y-full opacity-0" : "translate-y-0 opacity-100"
      }`}
    >
      <div className="max-w-4xl mx-auto bg-card border border-border rounded-xl shadow-2xl p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex p-2 rounded-lg bg-primary/10 shrink-0">
            <Cookie className="w-5 h-5 text-primary" />
          </div>
          
          <div className="flex-1 space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-foreground text-sm sm:text-base">
                  We value your privacy
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  We use cookies to analyze site usage and improve your experience. 
                  You can accept or reject analytics cookies.{" "}
                  <Link 
                    to="/privacy-policy" 
                    className="text-primary hover:underline"
                  >
                    Read our Privacy Policy
                  </Link>
                </p>
              </div>
              <button
                onClick={() => handleClose("rejected")}
                className="p-1 rounded-md hover:bg-muted transition-colors sm:hidden"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={() => handleClose("accepted")}
                size="sm"
                className="w-full sm:w-auto"
              >
                Accept Analytics
              </Button>
              <Button
                onClick={() => handleClose("rejected")}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
              >
                Reject Analytics
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
